import { getUid } from "../auth/AuthManager";
import OnevocaServerError from "../common/OnevocaServerError";
import getApiInstance from "../common/api";
import Group from "./Group";
import { auth } from "../../configurations/firebaseConfig";
import { sortedByNumbers } from "./groups";

export async function fetchGroups(): Promise<Group[]> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get(
      "/group/getGroupList.php",
      {
        params: {
          uid: uid,
        },
      }
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      const result = response.data.result;
      if (result === undefined) {
        return Promise.reject(new OnevocaServerError("No response."));
      }

      const groupsData = result.groups;
      const unspecifiedGroupData = result.unspecifiedGroup;
      const unspecifiedGroup = Group.fromJson(unspecifiedGroupData);
      unspecifiedGroup.isUnspecifiedGroup = true;

      let newGroups: Group[] = [];
      let realGroups: Group[] = [];

      newGroups.push(unspecifiedGroup);

      groupsData.forEach((groupData: any) => {
        const group = Group.fromJson(groupData);
        realGroups.push(group);
      });

      newGroups = [...newGroups, ...sortedByNumbers(realGroups)];

      return newGroups;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function addGroup(
  collectionId: number,
  groupname: string,
  languageCode: string
): Promise<void> {
  try {
    const uid = await getUid();
    const parameters = {
      uid: uid,
      collectionId: collectionId,
      title: groupname,
      language: languageCode,
    };
    const response = await getApiInstance(undefined).post(
      "/group/addGroup.php",
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateGroupCollection(
  groupId: number,
  collectionId: number
): Promise<void> {
  try {
    const uid = await getUid();
    const parameters = {
      uid: uid,
      groupId: groupId,
      collectionId: collectionId,
    };

    const response = await getApiInstance(undefined).post(
      "/group/updateCollection.php",
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateGroupsCollection(
  groupIds: number[],
  collectionId: number
): Promise<void> {
  try {
    const uid = await getUid();
    const parameters = {
      uid: uid,
      groupIds: groupIds,
      collectionId: collectionId,
    };

    const response = await getApiInstance(undefined).post(
      "/group/updateSelectedGroupListCollection.php",
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateGroup(
  beforeTitle: string,
  newTitle: string,
  newLanguage: string
): Promise<void> {
  try {
    const uid = await getUid();
    const parameters = {
      uid: uid,
      beforeTitle: beforeTitle,
      newTitle: newTitle,
      newLanguage: newLanguage,
    };

    const response = await getApiInstance(undefined).post(
      "/group/updateGroup.php",
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function mergeGroups(
  groups: string[],
  groupname: string,
  language: string
): Promise<void> {
  try {
    const uid = await getUid();
    const parameters: { [key: string]: any } = {};
    parameters["uid"] = uid;
    parameters["newgroup"] = groupname;
    parameters["lang"] = language;

    const groupParameters: { [key: string]: any } = {};
    groups.forEach((title, i) => {
      groupParameters[`${i}`] = title;
    });
    parameters["groups"] = groupParameters;

    const response = await getApiInstance(undefined).post(
      "/group/merge.php",
      parameters
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function deleteGroups(groups: string[]): Promise<void> {
  try {
    const uid = await getUid();
    const parameters: { [key: string]: any } = {};
    parameters["uid"] = uid;
    const groupParameters: { [key: string]: any } = {};

    groups.forEach((title, i) => {
      groupParameters[`${i}`] = title;
    });

    parameters["groups"] = groupParameters;

    const response = await getApiInstance(undefined).post(
      "/group/remove_array.php",
      parameters
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function deleteGroup(title: string): Promise<void> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get("/group/remove.php", {
      params: {
        uid: uid,
        gn: title,
      },
    });
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function fetchGroupLanguageCode(group: string): Promise<string> {
  const parameters = {
    uid: auth.currentUser?.providerData[0].uid,
    group: group,
  };

  try {
    const response = await getApiInstance().post(
      "/group/get_lang.php",
      parameters
    );
    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    const language = response.data.lang;
    if (language === undefined) {
      return Promise.reject(new Error("Not found language of group"));
    }
    return language;
  } catch (error) {
    return Promise.reject(error);
  }
}
